import type { Employment, IncomeSection } from "@credit-acceptance/step6-bff-client";

import {
  APPLICANT_SSN_TYPES,
  type ApplicantSsnType,
  EXTENSION_MASK_PLACEHOLDER,
  EXTENSION_TEXT_PLACEHOLDER,
  getDefaultGrossNet,
  isIncomeTypeAllowed,
  MAX_INCOMES_PER_PERSON,
  PHONE_MASK_PLACEHOLDER,
  stripMaskPlaceholder,
} from "./constants";
import { type IncomeInput, makeEmptyIncome } from "./Signers.schema";

// ---------------------------------------------------------------------------
// incomeMapping
//
// Maps the BFF `IncomeSection` (DscApplicantSection.income) onto the form's
// `IncomeInput` blocks. Pure functions — no React — so they stay unit-testable
// and reusable for both the Driver and 2nd Signer columns.
// ---------------------------------------------------------------------------

// BFF income-frequency enum value -> form `incomeFrequencyType` value.
const FREQUENCY_MAP: Record<string, NonNullable<IncomeInput["incomeFrequencyType"]>> = {
  WEEKLY: "Weekly",
  BI_WEEKLY: "Bi-Weekly",
  TWICE_MONTHLY: "2x-Monthly",
  MONTHLY: "Monthly",
  ANNUAL: "Annual",
};

// BFF income-frequency numeric id -> form `incomeFrequencyType` value. Some payloads
// only populate `incomeFrequencyTypeId`, leaving `incomeFrequencyType` null.
const ID_FREQUENCY_MAP: Record<number, NonNullable<IncomeInput["incomeFrequencyType"]>> = {
  1: "Weekly",
  2: "Bi-Weekly",
  3: "2x-Monthly",
  4: "Monthly",
  5: "Annual",
};

// Valid `employmentType` codes accepted by the schema (employment type ids 6-11).
const VALID_INCOME_TYPES = new Set<IncomeInput["employmentType"]>(["6", "7", "8", "9", "10", "11"]);

// Sentinel for a cleared income type. The schema types `employmentType` as a strict
// 6-11 enum, so an unselected value is represented as "" via a localized cast;
// the Select then renders blank, and a backend requiredViolation (see
// Signers.violations.ts) forces a reselection, rather than a client Zod message.
const UNSELECTED_INCOME_TYPE = "" as IncomeInput["employmentType"];

/**
 * Resolves the form `employmentType` from the BFF employment-type id, clamped to the
 * applicant's SSN/ITIN status. Falls back to "6" (Employee) when the id is
 * absent or outside the accepted 6-11 range, then clears the value (forcing a
 * reselect) when the resolved code is not allowed for a non-SSN applicant.
 * @param {number | undefined} employmentTypeId - BFF employment type id.
 * @param {ApplicantSsnType} applicantSsnType - The applicant's SSN/ITIN status.
 * @returns {IncomeInput["employmentType"]} A schema-valid income type code, or "" when cleared.
 */
function toIncomeType(
  employmentTypeId: number | undefined,
  applicantSsnType: ApplicantSsnType
): IncomeInput["employmentType"] {
  const code = String(employmentTypeId) as IncomeInput["employmentType"];
  const resolved = VALID_INCOME_TYPES.has(code) ? code : "6";
  return isIncomeTypeAllowed(resolved, applicantSsnType) ? resolved : UNSELECTED_INCOME_TYPE;
}

/**
 * Maps a single BFF `Employment` record onto a form income block.
 * @param {Employment} employment - A BFF employment record.
 * @param {ApplicantSsnType} applicantSsnType - The applicant's SSN/ITIN status (clamps income type).
 * @returns {IncomeInput} The hydrated income block, schema-valid for the form.
 */
export function mapEmploymentToIncome(
  employment: Employment,
  applicantSsnType: ApplicantSsnType = APPLICANT_SSN_TYPES.SSN
): IncomeInput {
  const employmentType = toIncomeType(employment.employmentTypeId, applicantSsnType);
  return {
    // Keep the BFF id when present so a future PUT can target the record; fall
    // back to a stable uuid purely for RHF's field-array key.
    id: employment.id ?? crypto.randomUUID(),
    employmentType,
    employerName: employment.employerName ?? "",
    occupation: employment.occupation ?? "",
    // Address — employer fields. `workLocations[]` has no dedicated UI today;
    // its presence only drives the "Different work location" flag below.
    streetNumber: employment.streetNumber ?? "",
    streetName: employment.streetName ?? "",
    apartmentNumber: employment.apartmentNumber ?? "",
    zip: employment.zip ?? "",
    city: employment.city ?? "",
    state: employment.state ?? "",
    employerPhone: stripMaskPlaceholder(employment.employerPhone ?? "", PHONE_MASK_PLACEHOLDER),
    phoneExtension: stripMaskPlaceholder(
      employment.phoneExtension ?? "",
      EXTENSION_MASK_PLACEHOLDER,
      EXTENSION_TEXT_PLACEHOLDER
    ),
    differentWorkLocation: Boolean(employment.workLocations?.length),
    monthlyGrossPayAmount: employment.monthlyGrossPayAmount ?? 0,
    hireDate: employment.hireDate ?? "",
    incomePerPayPeriodAmount: employment.incomePerPayPeriodAmount ?? undefined,
    incomeFrequencyType: employment.incomeFrequencyType
      ? FREQUENCY_MAP[employment.incomeFrequencyType]
      : employment.incomeFrequencyTypeId
        ? ID_FREQUENCY_MAP[employment.incomeFrequencyTypeId]
        : undefined,
    // Falls back to the type's own default (Net for Fixed Income) rather than
    // undefined, so a persisted Fixed Income record with no stored payType
    // hydrates correctly instead of reproducing the blank-dropdown bug (OR-23629).
    payType: employment.payType ?? getDefaultGrossNet(employmentType),
    notes: employment.notes ?? "",
    // Income Calculator fields (additive optional). The BFF sends `null` (not
    // an omitted key) when no pay-stub data is on file — normalize to
    // `undefined` here since the schema's `.optional()` rejects `null`.
    payStubDate: employment.payStubDate ?? undefined,
    payPeriodEndDate: employment.payPeriodEndDate ?? undefined,
    yearToDateIncomeAmount: employment.yearToDateIncomeAmount ?? undefined,
  };
}

/**
 * Maps the applicant's `IncomeSection` onto the form's income blocks. Orders by
 * `displayOrder` and caps at the per-person maximum. Returns a single blank
 * block when there are no employments so the column always renders one editable
 * block. The SSN/ITIN status clamps each hydrated income type and seeds the
 * blank block's default.
 * @param {IncomeSection | undefined} income - The applicant's BFF income section.
 * @param {ApplicantSsnType} applicantSsnType - The applicant's SSN/ITIN status.
 * @returns {IncomeInput[]} One block per employment, or a single blank block.
 */
export function mapIncomeSectionToIncomes(
  income?: IncomeSection,
  applicantSsnType: ApplicantSsnType = APPLICANT_SSN_TYPES.SSN
): IncomeInput[] {
  const employments = (income?.employments ?? [])
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
    .slice(0, MAX_INCOMES_PER_PERSON);

  return employments.length > 0
    ? employments.map((employment) => mapEmploymentToIncome(employment, applicantSsnType))
    : [makeEmptyIncome(applicantSsnType)];
}
